import autoBind from 'auto-bind';
import { Voice, YouTubeVideoId } from '../../../../common/src/model.ts';
import { NetworkError } from '../../util/network.ts';
import { AudioPlayer } from '../audio_player.ts';
import {
  loadYouTubeIframeApi,
  youTubeEmbedHost,
  YouTubeNamespace,
  YouTubePlayer,
  YouTubePlayerDataEvent,
  YouTubePlayerState,
} from './youtube_iframe_api.ts';

// a part's video this close in length to the one before it is taken to be the same arrangement, so switching
// parts keeps the place (GitHub issue #4041: "guess from the lengths")
const sameLengthToleranceSeconds = 1.5;
// how long a part switch waits for the new video's length before starting it from the top
const switchPollIntervalMs = 250;
const switchPollAttempts = 20;

// How long YouTube may take to start playing after play() before the sequencer is told it did not. playVideo() is
// a message to YouTube's iframe, which a mobile browser may refuse outside a tap (an auto-advance into the song, a
// repeat, the play once the song is ready), and then nothing reports that the video stayed still.
const defaultPlayConfirmationMs = 1_500;

// YouTube's onError codes for a video it will never play here, whatever the connection: an invalid id (2), a
// removed or private video (100) and one whose owner does not allow embedding (101, 150). Any other code is a
// load failure, which a reconnect may cure.
const unavailableVideoErrorCodes = new Set([2, 100, 101, 150]);

export function practiceVideoNetworkError(youTubeErrorCode: number): NetworkError {
  return unavailableVideoErrorCodes.has(youTubeErrorCode)
    ? NetworkError.VideoUnavailable
    : NetworkError.VideoLoadFailed;
}

export interface PracticeVideoData {
  videoId: YouTubeVideoId;
  channelName: string;
}

interface PendingSwitch {
  time: number;
  duration: number | undefined;
  wasPlaying: boolean;
}

/**
 * A YouTube practice video as the sequencer's audio player (GitHub issue #4041): the transport, the media
 * session and the stopwatch drive it like any other player. The video also has YouTube's own controls, so a
 * play or pause there is reported through `onExternalPlayingChange` for the sequencer to follow.
 */
export class YouTubeVideoPlayer implements AudioPlayer {
  readonly initialTime = '0:00';
  readonly appliesChangesLive = false;
  readonly fileTempo = undefined;
  readonly ticksPerBeat = undefined;

  private videoId: YouTubeVideoId;
  // the video the YouTube player was created with or last told to load
  private loadedVideoId: YouTubeVideoId | undefined;
  private player: YouTubePlayer | undefined;
  private ready = false;
  private destroyed = false;
  private state: number = YouTubePlayerState.Unstarted;
  // set by play() and by YouTube playing, cleared by a pause or the end: buffering mid-play still counts as playing
  private playIntent = false;
  private _playbackRate: number;
  private reportedDuration: number | undefined;
  private pendingSwitch: PendingSwitch | undefined;
  private switchPoll: ReturnType<typeof setInterval> | undefined;
  private readonly playConfirmationMs: number;
  private playConfirmation: ReturnType<typeof setTimeout> | undefined;
  // the child of the host YouTube's iframe lives in; removed on destroy
  private readonly element: HTMLDivElement;
  private readonly songReady: () => void;
  private readonly onDurationChange: (duration: number | undefined) => void;
  private readonly onNetworkError: (networkError: NetworkError) => void;
  private readonly endOfFile: () => void;
  private readonly onExternalPlayingChange: (playing: boolean) => void;
  private readonly updateLocationSlider?: () => void;
  private readonly onVideoDataChange?: (data: PracticeVideoData) => void;
  private reportedVideoData: PracticeVideoData | undefined;

  constructor({
    videoId,
    host,
    loadApi = loadYouTubeIframeApi,
    playbackRate,
    songReady,
    onDurationChange,
    onNetworkError,
    endOfFile,
    onExternalPlayingChange,
    updateLocationSlider,
    onVideoDataChange,
    playConfirmationMs = defaultPlayConfirmationMs,
  }: {
    videoId: YouTubeVideoId;
    host: HTMLElement;
    loadApi?: () => Promise<YouTubeNamespace>;
    playbackRate: number;
    songReady: () => void;
    onDurationChange: (duration: number | undefined) => void;
    onNetworkError: (networkError: NetworkError) => void;
    endOfFile: () => void;
    onExternalPlayingChange: (playing: boolean) => void;
    updateLocationSlider?: () => void;
    // the channel of the loaded video, once YouTube knows it, for the video's badge
    onVideoDataChange?: (data: PracticeVideoData) => void;
    // how long play() waits for YouTube to say it is playing before reporting that it is not
    playConfirmationMs?: number;
  }) {
    autoBind(this);
    this.videoId = videoId;
    this.playConfirmationMs = playConfirmationMs;
    this._playbackRate = playbackRate;
    this.songReady = songReady;
    this.onDurationChange = onDurationChange;
    this.onNetworkError = onNetworkError;
    this.endOfFile = endOfFile;
    this.onExternalPlayingChange = onExternalPlayingChange;
    this.updateLocationSlider = updateLocationSlider;
    this.onVideoDataChange = onVideoDataChange;
    this.element = document.createElement('div');
    this.element.style.width = '100%';
    this.element.style.height = '100%';
    host.appendChild(this.element);
    // YouTube replaces this with its iframe
    const target = document.createElement('div');
    this.element.appendChild(target);
    void this.load(loadApi, target);
  }

  private async load(loadApi: () => Promise<YouTubeNamespace>, target: HTMLElement) {
    let YT: YouTubeNamespace;
    try {
      YT = await loadApi();
    } catch {
      if (!this.destroyed) {
        this.onNetworkError(NetworkError.VideoLoadFailed);
      }
      return;
    }
    if (this.destroyed) {
      return;
    }
    this.loadedVideoId = this.videoId;
    this.player = new YT.Player(target, {
      host: youTubeEmbedHost,
      videoId: this.videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        playsinline: 1,
        rel: 0,
        controls: 1,
        enablejsapi: 1,
        iv_load_policy: 3,
        origin: window.location.origin,
      },
      events: {
        onReady: this.onReady,
        onStateChange: this.onStateChange,
        onError: this.onError,
      },
    });
  }

  // the YouTube player, once it takes calls
  private get readyPlayer(): YouTubePlayer | undefined {
    return this.ready ? this.player : undefined;
  }

  private onReady() {
    const player = this.player;
    if (this.destroyed || !player) {
      return;
    }
    this.ready = true;
    // YouTube's own full-screen button works as is: the API creates its iframe with allowfullscreen
    player.setPlaybackRate(this._playbackRate);
    // a part chosen while the player was still loading
    if (this.loadedVideoId !== this.videoId) {
      this.loadedVideoId = this.videoId;
      player.cueVideoById(this.videoId);
    }
    this.reportDuration();
    this.reportVideoData();
    this.songReady();
  }

  private onStateChange({ data }: YouTubePlayerDataEvent) {
    if (this.destroyed) {
      return;
    }
    this.state = data;
    // playing confirms a play(), and a pause or the end reports itself. Buffering, unstarted and cued settle
    // nothing: a refused play can buffer and then fall back to unstarted, so the state is checked when the wait is
    // over instead.
    if (
      data === YouTubePlayerState.Playing ||
      data === YouTubePlayerState.Paused ||
      data === YouTubePlayerState.Ended
    ) {
      this.stopPlayConfirmation();
    }
    switch (data) {
      case YouTubePlayerState.Playing:
        this.playIntent = true;
        this.onExternalPlayingChange(true);
        break;
      case YouTubePlayerState.Paused:
        this.playIntent = false;
        this.onExternalPlayingChange(false);
        break;
      case YouTubePlayerState.Ended:
        this.playIntent = false;
        this.endOfFile();
        break;
    }
    this.reportVideoData();
    if (!this.pendingSwitch) {
      this.reportDuration();
    } else if (data === YouTubePlayerState.Cued || data === YouTubePlayerState.Playing) {
      // the new video has taken over: its length is now its own, not the old one's
      this.settleSwitch();
    }
  }

  private onError({ data }: YouTubePlayerDataEvent) {
    if (!this.destroyed) {
      this.onNetworkError(practiceVideoNetworkError(data));
    }
  }

  private reportDuration() {
    const duration = this.duration;
    if (duration === this.reportedDuration) {
      return;
    }
    this.reportedDuration = duration;
    this.onDurationChange(duration);
  }

  // the loaded video's channel, which YouTube knows once the video's data has arrived
  get channelName(): string | undefined {
    return this.readyPlayer?.getVideoData().author || undefined;
  }

  private reportVideoData() {
    const videoId = this.readyPlayer?.getVideoData().video_id;
    const channelName = this.channelName;
    if (!videoId || !channelName) {
      return;
    }
    if (this.reportedVideoData?.videoId === videoId && this.reportedVideoData.channelName === channelName) {
      return;
    }
    this.reportedVideoData = { videoId, channelName };
    this.onVideoDataChange?.(this.reportedVideoData);
  }

  get duration(): number | undefined {
    const duration = this.readyPlayer?.getDuration();
    return duration ? duration : undefined;
  }

  get playing(): boolean {
    return (
      this.state === YouTubePlayerState.Playing || (this.state === YouTubePlayerState.Buffering && this.playIntent)
    );
  }

  get playbackRate(): number {
    return this._playbackRate;
  }

  getCurrentPercent(): number | undefined {
    const duration = this.duration;
    const player = this.readyPlayer;
    if (!duration || !player) {
      return;
    }
    return (100 * player.getCurrentTime()) / duration;
  }

  // The sequencer marks itself playing at once, so a tap answers without waiting on YouTube; if YouTube has not
  // started playing (or buffering) after playConfirmationMs, the sequencer is told it is not playing after all.
  // A player that is not ready yet waits for none: the sequencer plays again once the song is ready.
  play() {
    this.playIntent = true;
    const player = this.readyPlayer;
    if (!player) {
      return;
    }
    player.playVideo();
    this.stopPlayConfirmation();
    this.playConfirmation = setTimeout(() => {
      this.playConfirmation = undefined;
      if (this.destroyed || this.playing) {
        return;
      }
      this.playIntent = false;
      this.onExternalPlayingChange(false);
    }, this.playConfirmationMs);
  }

  private stopPlayConfirmation() {
    if (this.playConfirmation !== undefined) {
      clearTimeout(this.playConfirmation);
      this.playConfirmation = undefined;
    }
  }

  pause() {
    this.playIntent = false;
    this.stopPlayConfirmation();
    this.readyPlayer?.pauseVideo();
  }

  setNewTime(percent: number) {
    this.seekToPercent(percent);
  }

  setLocationPercentage(percent: number) {
    this.seekToPercent(percent);
  }

  restart() {
    this.seek(0);
    this.updateLocationSlider?.();
  }

  private seekToPercent(percent: number) {
    const duration = this.duration;
    if (duration) {
      this.seek((percent / 100) * duration);
    }
  }

  private seek(seconds: number) {
    const player = this.readyPlayer;
    if (!player || Math.abs(player.getCurrentTime() - seconds) < 0.01) {
      return;
    }
    // YouTube starts a cued or finished video playing when it is seeked; cueing it at the time keeps it still
    if (this.isStopped) {
      player.cueVideoById(this.videoId, seconds);
    } else {
      player.seekTo(seconds, true);
    }
  }

  private get isStopped(): boolean {
    return (
      this.state === YouTubePlayerState.Unstarted ||
      this.state === YouTubePlayerState.Cued ||
      this.state === YouTubePlayerState.Ended
    );
  }

  setPlaybackRate(rate: number) {
    this._playbackRate = rate;
    this.readyPlayer?.setPlaybackRate(rate);
  }

  /**
   * Switch to another part's video on the same YouTube player. When the new video is within 1.5 s of the old
   * one's length it carries on from the same place; otherwise it starts from the top. Playing stays playing.
   */
  setVideoId(videoId: YouTubeVideoId) {
    if (videoId === this.videoId) {
      return;
    }
    this.videoId = videoId;
    const player = this.readyPlayer;
    if (!player) {
      return; // onReady cues it
    }
    const wasPlaying = this.playIntent;
    this.pendingSwitch = { time: player.getCurrentTime(), duration: this.duration, wasPlaying };
    this.loadedVideoId = videoId;
    if (wasPlaying) {
      player.loadVideoById(videoId);
    } else {
      player.cueVideoById(videoId);
    }
    this.stopSwitchPoll();
    let attempts = 0;
    // the new length usually arrives with a state change; YouTube does not always send one for a cued video
    this.switchPoll = setInterval(() => {
      attempts += 1;
      this.settleSwitch({ giveUp: attempts >= switchPollAttempts });
    }, switchPollIntervalMs);
  }

  private settleSwitch({ giveUp = false }: { giveUp?: boolean } = {}) {
    const pending = this.pendingSwitch;
    const player = this.readyPlayer;
    if (!pending || !player) {
      return;
    }
    const duration = player.getDuration();
    if (!duration && !giveUp) {
      return;
    }
    this.pendingSwitch = undefined;
    this.stopSwitchPoll();
    const keepsPlace =
      !!duration &&
      pending.duration !== undefined &&
      Math.abs(duration - pending.duration) <= sameLengthToleranceSeconds;
    if (pending.wasPlaying) {
      if (keepsPlace) {
        player.seekTo(pending.time, true);
      }
      player.playVideo();
    } else if (keepsPlace) {
      player.cueVideoById(this.videoId, pending.time);
    }
    player.setPlaybackRate(this._playbackRate);
    this.reportDuration();
    this.updateLocationSlider?.();
  }

  private stopSwitchPoll() {
    if (this.switchPoll !== undefined) {
      clearInterval(this.switchPoll);
      this.switchPoll = undefined;
    }
  }

  // the practice video has no loop, volumes, tempo or first notes: its speed is setPlaybackRate
  setLoop(_loop: boolean) {}

  onVolumeChange() {}

  onTempoChange() {}

  playFirstNote(_voice: Voice, _startTick?: number) {}

  destroy() {
    this.destroyed = true;
    this.ready = false;
    this.pendingSwitch = undefined;
    this.stopSwitchPoll();
    this.stopPlayConfirmation();
    this.player?.destroy();
    this.player = undefined;
    this.element.remove();
  }
}
